import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {CURRENT_VIEW_TOKEN, CurrentView, ViewData} from '../../view';
import {Observable, of, Subject} from 'rxjs';
import {catchError, filter, first, map, switchMap, takeUntil} from 'rxjs/operators';
import {EntityType} from 'smag-commons/cfg/hierarchy';
import {EntityNetworkType as SmagEntityNetworkType} from 'smag-commons/dto/api';
import {SmagApiService} from '../../api/smag-api.service';

@Component({
    selector: 'app-ec-container',
    templateUrl: './ec-container.component.html',
    styleUrls: ['./ec-container.component.scss']
})
export class ECContainerComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();

    ecResource: HierarchyEntity | null; // `null` if not an energy community
    pageTitle: string | null; // title of sub-page

    constructor(@Inject(CURRENT_VIEW_TOKEN) private readonly currentView: CurrentView<ViewData>,
                private readonly hierarchyService: HierarchyService,
                private readonly apiService: SmagApiService) {
        this.ecResource = null;
        this.pageTitle = null;
    }

    ngOnInit() {
        // NOTE: community's path may change due to renaming in child component!
        this.hierarchyService.selection.pipe(
            filter(resource => !this.ecResource || resource?.id !== this.ecResource.id || resource?.path !== this.ecResource.path),
            switchMap(resource => !resource || resource.type === EntityType.ENERGY_COMMUNITY ?
                of(resource) : // either no resource or an energy community is selected
                this.getResourceCommunity(resource.id)), // something that *could* be part of an energy community is selected
            takeUntil(this.destroyed)
        ).subscribe(ecResource => {
            this.ecResource = ecResource;
        });

        this.currentView.viewData.pipe(
            map(data => data.title || null),
            takeUntil(this.destroyed)
        ).subscribe(pageTitle => {
            this.pageTitle = pageTitle;
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    private getResourceCommunity(resourceId: string): Observable<HierarchyEntity | null> {
        return this.apiService.getNetworkOfResource(resourceId).pipe(
            map(networkInfo => {
                if (networkInfo?.type !== SmagEntityNetworkType.ENERGY_COMMUNITY) {
                    throw new Error(`Bad type ${networkInfo.type} for network ${networkInfo.network_id}`);
                }
                return networkInfo.network_id;
            }),
            switchMap(networkId => networkId ?
                this.hierarchyService.findEntityById(networkId).pipe(first()) :
                of(null)),
            catchError(err => {
                console.error(`Resource ${resourceId} is not part of an energy community`, err);
                return of(null);
            })
        );
    }
}
